/**
 * TypeScript-порт Python-скрипта docs/analyze.py
 * Чистые функции без зависимостей от DOM/React — пригодны для Web Worker.
 */

import { ChatAnalyze } from "../interfaces"

// ── Утилиты ──────────────────────────────────────────────────────────────────

export function getText(msg: ChatAnalyze.TgMessage): string {
  const t = msg.text
  if (typeof t === "string") return t
  if (Array.isArray(t)) {
    return t
      .map((part) => (typeof part === "string" ? part : (part.text ?? "")))
      .join("")
  }
  return ""
}

function parseDate(s: string): Date {
  return new Date(s)
}

export function getDay(d: Date): string {
  return d.toISOString().slice(0, 10)
}

export function getWeek(d: Date): string {
  // ISO week: YYYY-Www
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const day = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() + 4 - day)
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
  const weekNo = Math.ceil(
    ((date.valueOf() - yearStart.valueOf()) / 86400000 + 1) / 7,
  )
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`
}

export function getMonth(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
}

export function mean(arr: number[]): number {
  if (!arr.length) return 0
  return arr.reduce((a, b) => a + b, 0) / arr.length
}

export function stddev(arr: number[], avg: number): number {
  if (!arr.length) return 0
  return Math.sqrt(arr.reduce((s, x) => s + (x - avg) ** 2, 0) / arr.length)
}

// ── Fraud-эвристики ──────────────────────────────────────────────────────────

export function calcFraudScore(params: {
  botShare: number
  singleMsgShare: number
  top1Share: number
  reactionRate: number
  total: number
  stickiness: number
  nightShare: number
  duplicateRatio: number
  top1AvgDay: number
  hourCv: number
}): {
  score: number
  flags: ChatAnalyze.FraudFlag[]
  verdict: ChatAnalyze.Verdict
} {
  const {
    botShare,
    singleMsgShare,
    top1Share,
    reactionRate,
    total,
    stickiness,
    nightShare,
    duplicateRatio,
    top1AvgDay,
    hourCv,
  } = params

  let score = 0
  const flags: ChatAnalyze.FraudFlag[] = []

  const ff = (
    label: string,
    pts: number,
    severity: "danger" | "warn" | "info",
  ) => {
    score += pts
    flags.push({ label, pts, severity })
  }

  const bs = botShare * 100
  if (bs > 40)
    ff(
      `Боты/каналы пишут ${bs.toFixed(1)}% сообщений (>40% — плохой знак)`,
      35,
      "danger",
    )
  else if (bs > 20)
    ff(
      `Боты/каналы: ${bs.toFixed(1)}% трафика (умеренно, норма <10%)`,
      15,
      "warn",
    )
  else if (bs > 8)
    ff(`Боты/каналы: ${bs.toFixed(1)}% трафика (допустимо)`, 5, "info")

  const sing = singleMsgShare * 100
  if (sing > 60)
    ff(
      `${sing.toFixed(1)}% пользователей написали ровно 1 сообщение (>60%)`,
      25,
      "danger",
    )
  else if (sing > 40)
    ff(`${sing.toFixed(1)}% пользователей написали 1 сообщение`, 10, "warn")

  const t1 = top1Share * 100
  if (t1 > 35)
    ff(`Топ-1 автор пишет ${t1.toFixed(1)}% всех сообщений`, 20, "danger")
  else if (t1 > 20) ff(`Топ-1 автор доминирует: ${t1.toFixed(1)}%`, 8, "warn")

  if (reactionRate < 0.01 && total > 300)
    ff(
      `Реакций почти нет: ${reactionRate.toFixed(4)}/сообщение (для городских чатов норма 0.03+)`,
      15,
      "danger",
    )
  else if (reactionRate > 8.0)
    ff(
      `Аномально много реакций: ${reactionRate.toFixed(2)}/сообщение`,
      20,
      "danger",
    )

  if (stickiness < 5)
    ff(
      `Stickiness ${stickiness.toFixed(1)}% — аудитория не возвращается`,
      15,
      "danger",
    )

  if (nightShare > 25)
    ff(
      `${nightShare.toFixed(1)}% сообщений ночью (00-07) — возможна автоматизация`,
      15,
      "danger",
    )
  else if (nightShare > 18)
    ff(`Ночная активность: ${nightShare.toFixed(1)}%`, 7, "warn")

  if (duplicateRatio > 25)
    ff(`${duplicateRatio.toFixed(1)}% дублирующихся сообщений`, 20, "danger")
  else if (duplicateRatio > 12)
    ff(
      `${duplicateRatio.toFixed(1)}% дублирующихся сообщений (умеренно)`,
      8,
      "warn",
    )

  if (top1AvgDay > 100)
    ff(
      `Топ-1 пишет ~${Math.round(top1AvgDay)} сообщений/день — скорее всего бот`,
      20,
      "danger",
    )
  else if (top1AvgDay > 50)
    ff(`Топ-1 пишет ~${Math.round(top1AvgDay)} сообщений/день`, 8, "warn")

  if (hourCv < 0.3 && total > 500)
    ff(
      `Активность по часам слишком равномерна (CV=${hourCv.toFixed(2)}) — возможна автоматизация`,
      10,
      "warn",
    )

  score = Math.min(score, 100)

  let verdict: ChatAnalyze.Verdict
  if (score <= 10)
    verdict = {
      label: "Низкий риск",
      type: "success",
      recommendation: "✅ Можно рассматривать для покупки рекламы",
    }
  else if (score < 30)
    verdict = {
      label: "Умеренно низкий риск",
      type: "success",
      recommendation: "🤔 Рекомендуется дополнительная проверка",
    }
  else if (score < 50)
    verdict = {
      label: "Умеренный риск",
      type: "warning",
      recommendation: "⚠️ Требует осторожности",
    }
  else if (score < 70)
    verdict = {
      label: "Высокий риск",
      type: "danger",
      recommendation: "❌ Не рекомендуется без аудита",
    }
  else
    verdict = {
      label: "Критический риск",
      type: "danger",
      recommendation: "🚫 Скорее всего накрутка — отказать",
    }

  return { score, flags, verdict }
}

// ── Главная функция анализа ───────────────────────────────────────────────────

export function collectStats(data: ChatAnalyze.TgExport): ChatAnalyze.Stats {
  const messages = data.messages ?? []
  const realMsgs = messages.filter((m) => m.type === "message")
  const svcMsgs = messages.filter((m) => m.type === "service")
  const total = realMsgs.length

  // Временные паттерны
  const dayUsers: Record<string, Set<string>> = {}
  const weekUsers: Record<string, Set<string>> = {}
  const monthUsers: Record<string, Set<string>> = {}
  const dayMsgs: Record<string, number> = {}
  const weekMsgs: Record<string, number> = {}
  const monthMsgs: Record<string, number> = {}
  const hourMsgs: Record<string, number> = {}
  const weekdayMsgs: Record<string, number> = {}

  // Авторы
  const authorMsgs: Record<string, ChatAnalyze.TgMessage[]> = {}
  const authorName: Record<string, string> = {}
  const botIds = new Set<string>()

  const dates: Date[] = []

  for (const m of realMsgs) {
    const dt = parseDate(m.date)
    dates.push(dt)

    const fid = m.from_id ?? "unknown"
    const fname = m.from ?? ""

    if (!authorMsgs[fid]) authorMsgs[fid] = []
    authorMsgs[fid].push(m)
    if (fname) authorName[fid] = fname
    if (fname.toLowerCase().includes("bot") || fid.startsWith("channel")) {
      botIds.add(fid)
    }

    const day = getDay(dt)
    const week = getWeek(dt)
    const month = getMonth(dt)
    const hour = String(dt.getHours())
    const wd = String(dt.getDay() === 0 ? 6 : dt.getDay() - 1) // 0=Пн, 6=Вс

    if (!dayUsers[day]) dayUsers[day] = new Set()
    if (!weekUsers[week]) weekUsers[week] = new Set()
    if (!monthUsers[month]) monthUsers[month] = new Set()

    dayUsers[day].add(fid)
    weekUsers[week].add(fid)
    monthUsers[month].add(fid)

    dayMsgs[day] = (dayMsgs[day] ?? 0) + 1
    weekMsgs[week] = (weekMsgs[week] ?? 0) + 1
    monthMsgs[month] = (monthMsgs[month] ?? 0) + 1
    hourMsgs[hour] = (hourMsgs[hour] ?? 0) + 1
    weekdayMsgs[wd] = (weekdayMsgs[wd] ?? 0) + 1
  }

  dates.sort((a, b) => a.valueOf() - b.valueOf())
  const dateMin = dates[0] ?? new Date()
  const dateMax = dates[dates.length - 1] ?? new Date()

  const totalDays = Math.max(
    Math.round((dateMax.valueOf() - dateMin.valueOf()) / 86400000) + 1,
    1,
  )
  const totalWeeks = Math.max(Math.ceil(totalDays / 7), 1)
  const totalMonths = Math.max(
    (dateMax.getFullYear() - dateMin.getFullYear()) * 12 +
      dateMax.getMonth() -
      dateMin.getMonth() +
      1,
    1,
  )

  // DAU / WAU / MAU
  const dauV = Object.values(dayUsers).map((s) => s.size)
  const wauV = Object.values(weekUsers).map((s) => s.size)
  const mauV = Object.values(monthUsers).map((s) => s.size)

  const avgDau = mean(dauV)
  const maxDau = dauV.length ? Math.max(...dauV) : 0
  const avgWau = mean(wauV)
  const maxWau = wauV.length ? Math.max(...wauV) : 0
  const avgMau = mean(mauV)
  const maxMau = mauV.length ? Math.max(...mauV) : 0
  const stickiness = avgMau ? (avgDau / avgMau) * 100 : 0

  // Авторы — сортировка по количеству сообщений
  const msgPerUser = Object.entries(authorMsgs).map(([uid, msgs]) => ({
    uid,
    count: msgs.length,
  }))
  msgPerUser.sort((a, b) => b.count - a.count)

  const singleMsgUsers = Object.values(authorMsgs).filter(
    (l) => l.length === 1,
  ).length
  const singleMsgShare = Object.keys(authorMsgs).length
    ? singleMsgUsers / Object.keys(authorMsgs).length
    : 0

  const top1Share = msgPerUser[0] ? msgPerUser[0].count / total : 0
  const top5Share =
    msgPerUser.slice(0, 5).reduce((s, e) => s + e.count, 0) / (total || 1)

  // Топ-1 авторы: среднее сообщений в день
  let top1AvgDay = 0
  if (msgPerUser[0]) {
    const top1DayMsgs: Record<string, number> = {}
    for (const m of authorMsgs[msgPerUser[0].uid] ?? []) {
      const d = getDay(parseDate(m.date))
      top1DayMsgs[d] = (top1DayMsgs[d] ?? 0) + 1
    }
    const vals = Object.values(top1DayMsgs)
    top1AvgDay = vals.length ? mean(vals) : 0
  }

  const botMsgs = [...botIds].reduce(
    (s, b) => s + (authorMsgs[b]?.length ?? 0),
    0,
  )
  const botShare = total ? botMsgs / total : 0

  // Все авторы (для сохранения участников на бэкенд)
  const allUsers: ChatAnalyze.TopUser[] = msgPerUser.map((e) => ({
    uid: e.uid,
    name: authorName[e.uid] ?? e.uid,
    count: e.count,
    share: total ? e.count / total : 0,
    isBot: botIds.has(e.uid),
  }))

  // Топ-30 авторов (для отображения)
  const topUsers: ChatAnalyze.TopUser[] = allUsers.slice(0, 30)

  // Реакции
  let totalReactions = 0
  const reactionCounter: Record<string, number> = {}
  let msgsWithReactions = 0
  const reactorUsers = new Set<string>()

  for (const m of realMsgs) {
    const rxs = m.reactions ?? []
    if (rxs.length) {
      msgsWithReactions++
      for (const rx of rxs) {
        const cnt = rx.count ?? 0
        totalReactions += cnt
        reactionCounter[rx.emoji] = (reactionCounter[rx.emoji] ?? 0) + cnt
        for (const rec of rx.recent ?? []) {
          reactorUsers.add(rec.from_id ?? rec.from ?? "?")
        }
      }
    }
  }

  const reactionRate = total ? totalReactions / total : 0
  const reactionCoverage = total ? (msgsWithReactions / total) * 100 : 0

  // Типы медиа
  const mediaCounters: Record<string, number> = {}
  const mediaIcons: Record<string, string> = {
    фото: "📷",
    "файл/видео": "📎",
    видеосообщение: "🎥",
    голосовое: "🎙️",
    стикер: "🎭",
    опрос: "📊",
    геолокация: "📍",
  }

  for (const m of realMsgs) {
    if (m.photo) mediaCounters["фото"] = (mediaCounters["фото"] ?? 0) + 1
    if (m.file && !m.sticker_emoji)
      mediaCounters["файл/видео"] = (mediaCounters["файл/видео"] ?? 0) + 1
    if (m.media_type === "video_message")
      mediaCounters["видеосообщение"] =
        (mediaCounters["видеосообщение"] ?? 0) + 1
    if (m.media_type === "voice_message")
      mediaCounters["голосовое"] = (mediaCounters["голосовое"] ?? 0) + 1
    if (m.sticker_emoji)
      mediaCounters["стикер"] = (mediaCounters["стикер"] ?? 0) + 1
    if (m.poll) mediaCounters["опрос"] = (mediaCounters["опрос"] ?? 0) + 1
    if (m.location_information)
      mediaCounters["геолокация"] = (mediaCounters["геолокация"] ?? 0) + 1
  }

  const mediaTypes: ChatAnalyze.MediaType[] = Object.entries(mediaCounters)
    .sort((a, b) => b[1] - a[1])
    .map(([label, count]) => ({ label, icon: mediaIcons[label] ?? "•", count }))

  const totalMedia = Object.values(mediaCounters).reduce((s, c) => s + c, 0)
  const mediaShare = total ? (totalMedia / total) * 100 : 0
  const textOnly = total - totalMedia

  const msgsWithReply = realMsgs.filter(
    (m) => m.reply_to_message_id != null,
  ).length
  const forwarded = realMsgs.filter((m) => m.forwarded_from != null).length
  const replyRate = total ? (msgsWithReply / total) * 100 : 0

  // Средняя длина текста
  const textLengths = realMsgs.map((m) => getText(m).length)
  const avgTextLen = mean(textLengths)

  // Дубликаты
  const texts = realMsgs.map((m) => getText(m).trim()).filter(Boolean)
  const uniqueTexts = new Set(texts).size
  const duplicateRatio = texts.length
    ? ((texts.length - uniqueTexts) / texts.length) * 100
    : 0

  // Ночная активность (00:00–06:59)
  const nightMsgs = [0, 1, 2, 3, 4, 5, 6].reduce(
    (s, h) => s + (hourMsgs[String(h)] ?? 0),
    0,
  )
  const nightShare = total ? (nightMsgs / total) * 100 : 0

  // Коэффициент вариации по часам
  const hourCounts = Array.from(
    { length: 24 },
    (_, h) => hourMsgs[String(h)] ?? 0,
  )
  const hmean = mean(hourCounts)
  const hourCv = hmean ? stddev(hourCounts, hmean) / hmean : 0

  // Топ-5 дней
  const topDays = Object.entries(dayMsgs)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([date, count]) => ({ date, count }))

  // Недельная статистика для графика
  const weekStats: ChatAnalyze.WeekStat[] = Object.keys(weekMsgs)
    .sort()
    .map((week) => ({
      week,
      messages: weekMsgs[week] ?? 0,
      users: weekUsers[week]?.size ?? 0,
    }))

  // Fraud Score
  const {
    score: fraudScore,
    flags: fraudFlags,
    verdict,
  } = calcFraudScore({
    botShare,
    singleMsgShare,
    top1Share,
    reactionRate,
    total,
    stickiness,
    nightShare,
    duplicateRatio,
    top1AvgDay,
    hourCv,
  })

  return {
    chatName: data.name ?? "—",
    chatType: data.type ?? "—",
    chatId: data.id ?? "—",
    chatUsername: data.username ? `@${data.username}` : "",
    reportDate: new Date().toISOString(),

    dateMin: dateMin.toISOString(),
    dateMax: dateMax.toISOString(),
    totalDays,
    totalWeeks,
    totalMonths,

    total,
    svcMessages: svcMsgs.length,

    avgDau,
    maxDau,
    avgWau,
    maxWau,
    avgMau,
    maxMau,
    stickiness,

    allUsersCount: Object.keys(authorMsgs).length,
    botUsersCount: botIds.size,
    singleMsgUsers,
    singleMsgShare,
    avgMsgsPerActive: Object.keys(authorMsgs).length
      ? total / Object.keys(authorMsgs).length
      : 0,
    allUsers,
    topUsers,
    top1Share,
    top5Share,
    top1AvgDay,
    botShare,
    botMsgs,

    totalReactions,
    reactionCounter,
    msgsWithReactions,
    reactorUsersCount: reactorUsers.size,
    reactionRate,
    reactionCoverage,

    mediaTypes,
    totalMedia,
    mediaShare,
    textOnly,
    msgsWithReply,
    forwarded,
    replyRate,
    avgTextLen,
    duplicateRatio,

    nightShare,
    hourCv,
    hourMsgs,
    weekdayMsgs,
    weekStats,
    topDays,

    fraudScore,
    fraudFlags,
    verdict,
  }
}

// ── Хелперы для UI ────────────────────────────────────────────────────────────

/**
 * Возвращает MUI severity-цвет для KPI-карточки.
 * lowGood=true: маленькое значение — лучше (fraud score, боты)
 * lowGood=false: большое значение — лучше (DAU, stickiness)
 */
export function badgeColor(
  value: number,
  good: number,
  warn: number,
  _danger: number,
  lowGood = true,
): "success" | "warning" | "error" | "default" {
  if (lowGood) {
    if (value <= good) return "success"
    if (value <= warn) return "warning"
    return "error"
  } else {
    if (value >= good) return "success"
    if (value >= warn) return "warning"
    return "error"
  }
}

export function fmtNum(n: number, decimals = 0): string {
  return n.toLocaleString("ru-RU", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}
